import { formatCurrency, formatAmountInWords, formatDateDMY, deriveTransactionDetails } from "./LineGrid";

const VOUCHER_TYPE_LABELS = {
  RECEIPT: "Receipt", PAYMENT: "Payment", CONTRA: "Contra", JOURNAL: "Journal",
  PURCHASE: "Purchase / Vendor Bill", CREDIT_NOTE: "Credit Note", DEBIT_NOTE: "Debit Note",
  ADJUSTMENT: "Adjustment", OPENING: "Opening",
};

// Phase 5 "Enforce Payment Nature and Account Consistency" correction's
// professional categories, plus the pre-rename "vendor_payable" spelling for
// vouchers saved before that correction - kept in sync with
// VoucherViewPage.jsx's own copy of this map.
const PAYMENT_NATURE_LABELS = {
  direct_expense: "Direct Expense", payable_payment: "Payable Payment", vendor_payable: "Payable Payment",
  emergency_expense: "Emergency / No-Invoice Expense", salary_payment: "Salary Payment", reimbursement: "Staff / Member Reimbursement",
  advance_payment: "Advance Payment", capital_payment: "Asset / Capital Payment", refund: "Refund", other: "Other",
};

function ledgerLabel(line) {
  return line.ledgerName || line.bankAccountLabel || line.vendorLabel || "-";
}

/**
 * Phase 5 "Fix Voucher Printing" correction - the reusable, print-only
 * accounting-document renderer for a posted/draft/cancelled voucher.
 *
 * This is deliberately NOT a screenshot of the on-screen VoucherViewPage: it
 * is a separate, purpose-built document layout (header, transaction
 * details, accounting entry table, amount in words, signature blocks) built
 * from the same normalized voucher data every other accounting screen
 * already uses (deriveTransactionDetails, formatCurrency, etc.), so it never
 * drifts from what the app actually recorded and never hardcodes sample
 * values.
 *
 * Deliberately generic across voucher types (item 11): Payment/Receipt/
 * Contra get the business-friendly "Transaction Details" block via
 * deriveTransactionDetails; anything else (Journal, Purchase/Vendor Bill,
 * Credit/Debit Note, Adjustment, Opening) falls back to just the Accounting
 * Entry table plus the voucher's own narration - no voucher type needs its
 * own bespoke print component or its own print CSS.
 *
 * Rendering (visible vs. hidden) is controlled entirely by the caller via
 * Tailwind's `hidden print:block` utilities - this component itself renders
 * unconditionally so it's trivial to unit-test.
 */
export default function VoucherPrintLayout({ voucher, societyName, societyAddress, registrationNumber }) {
  if (!voucher) return null;
  const typeLabel = VOUCHER_TYPE_LABELS[voucher.voucherType] || voucher.voucherType;
  const status = voucher.status;
  const isDraft = status === "draft";
  const isCancelled = status === "cancelled";
  const isReversal = !!voucher.reversalOfVoucherId;
  const transactionDetails = deriveTransactionDetails(voucher);
  const totalDebit = voucher.totalDebit || 0;
  const totalCredit = voucher.totalCredit || 0;
  const lines = voucher.lines || [];

  return (
    <div id="voucher-print-area" className="voucher-print-document mx-auto max-w-[720px] bg-white p-2 text-[12px] leading-snug text-black">
      <div className="text-center">
        <div className="text-lg font-extrabold uppercase tracking-wide">{societyName || "Housing Society"}</div>
        {societyAddress && <div className="text-[11px] text-black">{societyAddress}</div>}
        {registrationNumber && <div className="text-[11px] text-black"><span className="font-semibold">Registration No:</span> {registrationNumber}</div>}
        <div className="mt-1 text-sm font-bold uppercase tracking-[0.14em]">{typeLabel} Voucher</div>
      </div>

      <hr className="my-2 border-black" />

      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
        <div><span className="font-semibold">Voucher No:</span> {voucher.voucherNumber || (isDraft ? "Not yet assigned (Draft)" : "-")}</div>
        <div><span className="font-semibold">Date:</span> {formatDateDMY(voucher.voucherDate)}</div>
        <div><span className="font-semibold">Financial Year:</span> {voucher.financialYearCode || "-"}</div>
        <div>
          <span className="font-semibold">Status:</span>{" "}
          {isDraft ? "DRAFT" : isCancelled ? (isReversal ? "REVERSED" : "CANCELLED") : "POSTED"}
        </div>
      </div>

      {/* Items 7 & 9: a draft or cancelled/reversed printout must never be
          visually confused with an active posted accounting document. */}
      {isDraft && (
        <div className="mt-2 border-2 border-black px-2 py-1 text-center text-sm font-extrabold uppercase tracking-widest">
          DRAFT — NOT POSTED. This has no accounting effect until posted.
        </div>
      )}
      {isCancelled && (
        <div className="mt-2 border-2 border-black px-2 py-1 text-center text-sm font-extrabold uppercase tracking-widest">
          {isReversal ? "REVERSED" : "CANCELLED"}{voucher.cancelReason ? ` — ${voucher.cancelReason}` : ""}
        </div>
      )}

      {transactionDetails && (
        <div className="mt-3">
          <div className="border-b border-black pb-0.5 text-xs font-bold uppercase tracking-[0.1em]">Transaction Details</div>
          <table className="mt-1 w-full">
            <tbody>
              {voucher.voucherType === "PAYMENT" ? (
                <>
                  <tr><td className="w-40 py-0.5 font-semibold align-top">Paid To:</td><td className="py-0.5">{transactionDetails.rows[0].value}</td></tr>
                  <tr><td className="py-0.5 font-semibold align-top">Payment For:</td><td className="py-0.5">{transactionDetails.rows[1].value}</td></tr>
                  <tr><td className="py-0.5 font-semibold align-top">Payment Nature:</td><td className="py-0.5">{PAYMENT_NATURE_LABELS[voucher.paymentNature] || "Other"}</td></tr>
                  <tr><td className="py-0.5 font-semibold align-top">Amount:</td><td className="py-0.5 font-bold">{formatCurrency(transactionDetails.amount)}</td></tr>
                  <tr><td className="py-0.5 font-semibold align-top">Paid From:</td><td className="py-0.5">{transactionDetails.rows[2].value}</td></tr>
                </>
              ) : (
                <>
                  {transactionDetails.rows.map((row) => (
                    <tr key={row.label}><td className="w-40 py-0.5 font-semibold align-top">{row.label}:</td><td className="py-0.5">{row.value}</td></tr>
                  ))}
                  <tr><td className="py-0.5 font-semibold align-top">Amount:</td><td className="py-0.5 font-bold">{formatCurrency(transactionDetails.amount)}</td></tr>
                </>
              )}
              {/* Phase 13: Contra now captures Payment Mode too, so it is no
                  longer excluded from the printed Transaction Details. */}
              {voucher.paymentMode && (
                <tr><td className="py-0.5 font-semibold align-top">Payment Mode:</td><td className="py-0.5 capitalize">{String(voucher.paymentMode).replace(/_/g, " ")}</td></tr>
              )}
              {voucher.referenceNo && (
                <tr><td className="py-0.5 font-semibold align-top">Cheque / Ref No.:</td><td className="py-0.5">{voucher.referenceNo}</td></tr>
              )}
              {voucher.narration && (
                <tr><td className="py-0.5 font-semibold align-top">Narration:</td><td className="py-0.5">{voucher.narration}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {!transactionDetails && voucher.narration && (
        <div className="mt-3">
          <span className="font-semibold">Narration:</span> {voucher.narration}
        </div>
      )}

      <div className="mt-3" style={{ pageBreakInside: "avoid" }}>
        <div className="border-b border-black pb-0.5 text-xs font-bold uppercase tracking-[0.1em]">
          {voucher.voucherType === "JOURNAL" ? "Journal Voucher" : "Accounting Entry"}
        </div>
        <table className="mt-1 w-full">
          <thead>
            <tr className="border-b border-black text-left text-[11px] font-semibold uppercase">
              <th className="py-0.5">{voucher.voucherType === "JOURNAL" ? "Particulars" : "Ledger"}</th>
              <th className="py-0.5 text-right">Dr</th>
              <th className="py-0.5 text-right">Cr</th>
            </tr>
          </thead>
          <tbody>
            {/* JOURNAL VOUCHER — SIMPLE TALLY-STYLE CORRECTION, item 10: the
                debit-first / indented "To <ledger>" credit layout below is
                deliberately scoped to voucherType === "JOURNAL" only - every
                other voucher type keeps the exact plain Ledger/Debit/Credit
                rows it always has (no visual change to Payment, Receipt,
                Contra, Purchase, Credit/Debit Note, Adjustment, Opening). */}
            {voucher.voucherType === "JOURNAL"
              ? [...lines].sort((a, b) => (b.debit > 0 ? 1 : 0) - (a.debit > 0 ? 1 : 0)).map((line) => (
                  <tr key={line.id} className="border-b border-dotted border-black/40">
                    <td className="py-0.5">
                      {line.debit > 0 ? ledgerLabel(line) : <span className="pl-6">To {ledgerLabel(line)}</span>}
                    </td>
                    <td className="py-0.5 text-right">{line.debit > 0 ? formatCurrency(line.debit) : ""}</td>
                    <td className="py-0.5 text-right">{line.credit > 0 ? formatCurrency(line.credit) : ""}</td>
                  </tr>
                ))
              : lines.map((line) => (
                  <tr key={line.id} className="border-b border-dotted border-black/40">
                    <td className="py-0.5">{ledgerLabel(line)}{line.partyName && !line.vendorLabel ? ` (${line.partyName})` : ""}</td>
                    <td className="py-0.5 text-right">{line.debit > 0 ? formatCurrency(line.debit) : ""}</td>
                    <td className="py-0.5 text-right">{line.credit > 0 ? formatCurrency(line.credit) : ""}</td>
                  </tr>
                ))}
            <tr className="border-t-2 border-black font-bold">
              <td className="py-1">Total</td>
              <td className="py-1 text-right">{formatCurrency(totalDebit)}</td>
              <td className="py-1 text-right">{formatCurrency(totalCredit)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mt-3">
        <span className="font-semibold">Amount in Words:</span> {formatAmountInWords(Math.max(totalDebit, totalCredit))}
      </div>

      <div className="mt-8 grid grid-cols-3 gap-4 text-center" style={{ pageBreakInside: "avoid" }}>
        <div>
          <div className="mb-8">&nbsp;</div>
          <div className="border-t border-black pt-1 text-[11px]">Prepared By</div>
        </div>
        <div>
          <div className="mb-8">&nbsp;</div>
          <div className="border-t border-black pt-1 text-[11px]">Approved By</div>
        </div>
        <div>
          <div className="mb-8">&nbsp;</div>
          <div className="border-t border-black pt-1 text-[11px]">Receiver Signature</div>
        </div>
      </div>
    </div>
  );
}
